import { ref } from 'vue';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';

export function useUpload() {
  const isUploading = ref(false);
  const uploadProgress = ref(0);
  const { showError } = useToast();

  // Comprimir imagen usando Canvas del navegador
  const compressImage = (file, maxWidth = 1024, maxHeight = 1024, quality = 0.8) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) return resolve(file);

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
      };
    });
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    isUploading.value = true;
    uploadProgress.value = 0;

    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('image', compressed);

      const response = await apiClient.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        },
      });

      if (response.data && response.data.file_path) {
        return response.data.file_path;
      }
      throw new Error("Respuesta de servidor inválida");
    } catch (error) {
      const msg = error.response?.data?.error || "Error al subir el archivo";
      showError(msg);
      return null;
    } finally {
      isUploading.value = false;
    }
  };

  const deleteFile = async (filePath) => {
    if (!filePath) return;
    try {
      await apiClient.post('/upload/delete', { file_path: filePath });
    } catch (error) {
      console.warn('No se pudo eliminar el archivo físico anterior:', error);
    }
  };

  return {
    uploadFile,
    deleteFile,
    isUploading,
    uploadProgress
  };
}